import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { initAnalytics, track } from '@/lib/analytics';
import { ANDROID_PLAY_STORE_URL, IOS_APP_STORE_URL } from '@/lib/store';
import iconSrc from '@/assets/marketing/icon.png';

const nunito = {
  fontFamily: "'Nunito', system-ui, sans-serif",
} as const;

const khula = {
  fontFamily: "'Khula', system-ui, sans-serif",
} as const;

type MarketingLayoutProps = {
  children: React.ReactNode;
  screen: string;
};

export function MarketingLayout({ children, screen }: MarketingLayoutProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    initAnalytics();
    track(screen, { route: pathname });
  }, [pathname, screen]);

  return (
    <div className="min-h-screen bg-white font-nunito text-hmong-ink">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-xl focus:bg-white focus:px-4 focus:py-2 focus:font-bold"
      >
        Skip to content
      </a>
      <header className="fixed left-0 right-0 top-0 z-50 bg-primary/95 backdrop-blur-sm">
        <nav className="mx-auto max-w-7xl px-4 py-3 sm:px-6 sm:py-4" aria-label="Hmonglus">
          <div className="flex items-center justify-between gap-3">
            <Link to="/" className="flex min-w-0 items-center gap-2.5 text-xl font-light leading-none tracking-tight text-white sm:gap-3 sm:text-2xl">
              <img src={iconSrc} alt="" className="block h-8 w-8 shrink-0 rounded-xl bg-white/95 shadow-sm sm:h-9 sm:w-9" />
              <span className="translate-y-px truncate leading-none" style={khula}>
                Hmonglus
              </span>
            </Link>
            <div className="flex shrink-0 items-center gap-3 sm:gap-5">
              <Link to="/auth/sign-in" className="font-medium text-white/90 transition-colors hover:text-white" style={nunito}>
                Log In
              </Link>
              <Link
                to="/app/resources"
                onClick={() => track('Landing CTA Press', { cta: 'header-learn' })}
                className="rounded-xl bg-white px-3.5 py-2 text-sm font-semibold text-primary hover:bg-white/90 sm:px-5 sm:text-base"
                style={nunito}
              >
                Start Learning
              </Link>
            </div>
          </div>
        </nav>
      </header>
      <div id="main">{children}</div>
      <footer className="bg-primary-dark py-12 text-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 grid grid-cols-2 gap-8 lg:grid-cols-4">
            <div className="col-span-2 lg:col-span-1">
              <p className="mb-4 text-2xl font-light" style={khula}>
                Hmonglus
              </p>
              <p className="leading-relaxed text-white/70" style={nunito}>
                Learn White Hmong and Hmong Leng with flashcards, stories, pronunciation, and games.
              </p>
            </div>
            <div>
              <h2 className="mb-4 font-semibold" style={nunito}>
                Learn
              </h2>
              <ul className="space-y-2 text-white/70" style={nunito}>
                <li>
                  <a href="/#features" className="transition-colors hover:text-white">
                    What's inside
                  </a>
                </li>
                <li>
                  <Link to="/app/resources" className="transition-colors hover:text-white">
                    Learning hub
                  </Link>
                </li>
                <li>
                  <Link to="/app/alphabet" className="transition-colors hover:text-white">
                    Alphabet
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="transition-colors hover:text-white">
                    Pricing
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="mb-4 font-semibold" style={nunito}>
                Get the app
              </h2>
              <ul className="space-y-2 text-white/70" style={nunito}>
                <li>
                  <a href={IOS_APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white">
                    App Store
                  </a>
                </li>
                <li>
                  <a href={ANDROID_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white">
                    Google Play
                  </a>
                </li>
                <li>
                  <Link to="/auth/sign-in" className="transition-colors hover:text-white">
                    Log In
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="mb-4 font-semibold" style={nunito}>
                Legal & Support
              </h2>
              <ul className="space-y-2 text-white/70" style={nunito}>
                <li>
                  <Link to="/support" className="transition-colors hover:text-white">
                    Support
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="transition-colors hover:text-white">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="transition-colors hover:text-white">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8">
            <p className="text-center text-white/50" style={nunito}>
              © {new Date().getFullYear()} Hmonglus. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
